/**
 * Viator bookable products for a city visit — mirrors iOS ViatorProductsService.
 * Web caches first-page results in localStorage (substitute for CityVisit.toursData).
 */

import { buildAuthHeaders } from './apiClient';
import { viatorProductsURL } from './planningApi';

export const VIATOR_PRODUCTS_DEFAULT_LIMIT = 100;

export interface TourPrice {
  currency: string;
  startingPrice: number;
}

export interface ViatorProduct {
  categories: string[];
  description: string;
  price: TourPrice;
  productCode: string;
  title: string;
  productUrl?: string | null;
  imageUrl?: string | null;
  isMeal?: boolean | null;
  matchedInterests?: string[] | null;
}

export type ToursCacheLoadAction = 'showCache' | 'fetchFirstPage' | 'showOfflineEmpty';

export interface ViatorProductsPage {
  products: ViatorProduct[];
  nextCursor: string | null;
}

function cacheKey(userId: string, cityVisitId: string): string {
  return `wt_viator_tours_${userId}_${cityVisitId.toLowerCase()}`;
}

export function isMealProduct(product: ViatorProduct): boolean {
  return product.isMeal === true;
}

export function toursLoadAction(
  hasCachedProducts: boolean,
  isReachable: boolean
): ToursCacheLoadAction {
  if (hasCachedProducts) return 'showCache';
  if (isReachable) return 'fetchFirstPage';
  return 'showOfflineEmpty';
}

export function getCachedViatorProducts(userId: string, cityVisitId: string): ViatorProduct[] {
  try {
    const raw = localStorage.getItem(cacheKey(userId, cityVisitId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isValidProduct);
  } catch {
    return [];
  }
}

export function setCachedViatorProducts(
  userId: string,
  cityVisitId: string,
  products: ViatorProduct[]
): void {
  localStorage.setItem(cacheKey(userId, cityVisitId), JSON.stringify(products));
}

function isValidProduct(value: unknown): value is ViatorProduct {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const o = value as Record<string, unknown>;
  return typeof o.productCode === 'string' && typeof o.title === 'string';
}

function normalizeProduct(raw: Record<string, unknown>): ViatorProduct | null {
  const productCode = typeof raw.productCode === 'string' ? raw.productCode : null;
  const title = typeof raw.title === 'string' ? raw.title : null;
  if (!productCode || !title) return null;

  const priceRaw = raw.price;
  let price: TourPrice = { currency: 'USD', startingPrice: 0 };
  if (priceRaw && typeof priceRaw === 'object' && !Array.isArray(priceRaw)) {
    const p = priceRaw as Record<string, unknown>;
    price = {
      currency: typeof p.currency === 'string' ? p.currency : 'USD',
      startingPrice: typeof p.startingPrice === 'number' ? p.startingPrice : 0,
    };
  }

  const categories = Array.isArray(raw.categories)
    ? raw.categories.filter((c): c is string => typeof c === 'string')
    : [];

  return {
    categories,
    description: typeof raw.description === 'string' ? raw.description : '',
    price,
    productCode,
    title,
    productUrl: typeof raw.productUrl === 'string' ? raw.productUrl : null,
    imageUrl: typeof raw.imageUrl === 'string' ? raw.imageUrl : null,
    isMeal: typeof raw.isMeal === 'boolean' ? raw.isMeal : null,
    matchedInterests: Array.isArray(raw.matchedInterests)
      ? raw.matchedInterests.filter((c): c is string => typeof c === 'string')
      : null,
  };
}

function nextCursorFromResponse(response: Response): string | null {
  const header = response.headers.get('Next-Cursor') ?? response.headers.get('next-cursor');
  if (!header || !header.trim()) return null;
  return header.trim();
}

export async function fetchViatorProductsPage(
  cityVisitId: string,
  options?: { limit?: number; cursor?: string | null }
): Promise<ViatorProductsPage> {
  const headers = await buildAuthHeaders(true);
  const url = viatorProductsURL(cityVisitId, {
    limit: options?.limit ?? VIATOR_PRODUCTS_DEFAULT_LIMIT,
    cursor: options?.cursor,
  });
  const response = await fetch(url, { method: 'GET', headers });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(text || `HTTP ${response.status}`);
  }

  const parsed = (await response.json()) as unknown;
  const list = Array.isArray(parsed) ? parsed : [];
  const products: ViatorProduct[] = [];
  for (const entry of list) {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) continue;
    const normalized = normalizeProduct(entry as Record<string, unknown>);
    if (normalized) products.push(normalized);
  }

  return {
    products,
    nextCursor: nextCursorFromResponse(response),
  };
}

/** Fetch first page and persist to localStorage cache. */
export async function fetchFirstPageAndCache(
  userId: string,
  cityVisitId: string
): Promise<ViatorProduct[]> {
  const page = await fetchViatorProductsPage(cityVisitId, {
    limit: VIATOR_PRODUCTS_DEFAULT_LIMIT,
    cursor: null,
  });
  setCachedViatorProducts(userId, cityVisitId, page.products);
  return page.products;
}

export function isOnline(): boolean {
  if (typeof navigator === 'undefined') return true;
  return navigator.onLine !== false;
}
